import { type InputHTMLAttributes, forwardRef, memo } from 'react'
import { cn } from '@/lib/utils'

export interface SwitchProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'onChange'
> {
  checked: boolean
  onChange: (checked: boolean) => void
  label?: string
}

const SwitchComponent = forwardRef<HTMLInputElement, SwitchProps>(
  ({ className, checked, onChange, label, id, ...props }, ref) => {
    return (
      <label
        htmlFor={id}
        className={cn(
          'inline-flex items-center gap-3 cursor-pointer group select-none transition-opacity hover:opacity-90 active:opacity-100',
          className,
        )}
      >
        <div className="relative inline-flex items-center">
          <input
            {...props}
            id={id}
            type="checkbox"
            className="sr-only peer"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            ref={ref}
          />
          <div
            className={cn(
              'w-10 h-6 bg-secondary/50 rounded-full border border-border transition-all duration-300 ease-in-out',
              'peer-checked:bg-primary peer-checked:border-primary',
              'group-hover:border-border/80 peer-focus-visible:ring-2 peer-focus-visible:ring-primary/20 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-background',
            )}
          />
          <div
            className={cn(
              'absolute left-1 top-1 w-4 h-4 bg-card rounded-full transition-all duration-300 ease-in-out shadow-sm transform-gpu',
              'peer-checked:translate-x-4 peer-checked:scale-95',
              'group-active:scale-90',
            )}
          />
        </div>
        {label && (
          <span className="text-sm font-medium text-foreground/90 transition-colors group-hover:text-foreground">
            {label}
          </span>
        )}
      </label>
    )
  },
)

SwitchComponent.displayName = 'Switch'

export const Switch = memo(SwitchComponent)
